import { useMutation, useQuery } from '@tanstack/react-query';
import { AvField, AvForm, AvGroup } from 'availity-reactstrap-validation';
import React, { useEffect, useState } from 'react';
import { withNamespaces } from 'react-i18next';
import {
    Modal, 
    ModalHeader, 
    ModalBody, 
    ModalFooter,
    Button, 
    FormGroup, 
    Label,
    Input,
    Spinner
} from 'reactstrap';
import { toast } from 'react-toastify';
import { 
    DocumentsCategoryTypes, 
    DocumentsConfidentialityLevels, 
    DocumentsTypes 
} from 'src/modules/data-protection/constants';
import DocumentsService from 'src/modules/data-protection/apis/DocumentsService';
import ISMSControls from './ISMSControls';
import MultiSelect from 'src/modules/data-protection/components/MultiSelect';
import CommonService from 'src/modules/data-protection/apis/CommonService';
import { connect } from 'react-redux';

const NewDocumentModal = ({ 
    organization,
    t, 
    isOpen, 
    toggle, 
    onComplete,
    documentToEdit
}) => {
    const [contentType, setContentType] = useState('file');
    const [file, setFile] = useState(null);
    const [ selectedControls, setSelectedControls ] = useState(null);
    const [ ismsControlsOptions, setIsmsControlsOptions ] = useState(null);
    const [ selectedLegalEntities, setSelectedLegalEntities ] = useState([]);

    const {
        mutate: addDocumentMutation,
        isLoading: addingDocumentInProcess
    } = useMutation({
        mutationFn: async (payload) => {
            const service = DocumentsService.getInstance();
            return await service.uploadDocument(payload);
        },
        onSuccess: () => {
            toast(t("Document added successfully."), {
                type: "success"
            });
            onComplete && onComplete();
            toggle();
        },
        onError: () => {
            toast(t("An error occurred while adding the document."), {
                type: "error"
            });
        }
    });

    const {
        mutate: editDocumentMutation,
        isLoading: editingDocumentInProcess
    } = useMutation({
        mutationFn: async (payload) => {
            const service = DocumentsService.getInstance();
            return await service.updateDocument(documentToEdit.id, payload);
        },
        onSuccess: () => {
            toast(t("Document updated successfully."), {
                type: "success"
            });
            onComplete && onComplete();
            toggle();
        },
        onError: () => {
            toast(t("An error occurred while updating the document."), {
                type: "error"
            });
        }
    });

    const {
        data: legalEntitiesOptions,
        isFetching: fetchingLegalEntities
    } = useQuery({
        queryKey: ["data-protection-enteties-list"],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.legalEntities(organization.id);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            toast(t("An error occurred while fetching legal entities."), {
                type: "error"
            });
        }
    });

    const handleSubmit = (e, values) => {
        e.preventDefault();

        const formData = new FormData();

        Object.entries(values).forEach(([key, value]) => {
            formData.append(key, value);
        });

        Object.values(selectedControls).flat().forEach(c => {
            formData.append('controls[]', c);
        });

        formData.delete('legalEntities');

        selectedLegalEntities.forEach(ent => {
            formData.append('legalEntities[]', ent.id);
        });

        formData.delete('file'); // Remove the file field added by AvForm for validation

        if (contentType === 'file' && file) {
            formData.append('file', file);
        }

        if (documentToEdit) {
            editDocumentMutation(formData)
        }
        else {
            addDocumentMutation(formData);
        }
    };

    useEffect(() => {
        if(isOpen){
            setFile(null);

            if (documentToEdit?.legalEntities?.length > 0) {
                setSelectedLegalEntities(documentToEdit?.legalEntities)
            }
            else {
                setSelectedLegalEntities([]);
            }

            if (documentToEdit?.controls) {
                const controlsToSelect = {};

                Object.keys(documentToEdit.controls).forEach((controleKey) => {
                    controlsToSelect[controleKey] = (documentToEdit.controls[controleKey] || []).map((control) => control.id)
                });

                setSelectedControls(controlsToSelect)
            }
            else {
                setSelectedControls(null)
            }

            setContentType(documentToEdit?.content_type || 'file');
        }
    }, [ 
        isOpen,
        documentToEdit,
        t
    ]);

    useEffect(() => {
        if ( selectedLegalEntities.length > 0 ) {
            setIsmsControlsOptions({
                legal_ids: selectedLegalEntities.map(((item) => item.id)).join(', ')
            })
        }
        else {
            setIsmsControlsOptions(null);
        }
    }, [ selectedLegalEntities ]);

    return (
        <Modal size='lg' isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>
                {documentToEdit ? t('Edit Document') : t('Add Document')}
            </ModalHeader>
            <AvForm 
                onValidSubmit={handleSubmit} 
                className="needs-validation"
            >
                <ModalBody>
                    <AvGroup className='mb-3'>
                        <Label for="title">
                            {t('Title')}
                        </Label>

                        <AvField
                            id="title"
                            name="title"
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true }
                            }}
                            defaultValue={documentToEdit?.title}
                        />
                    </AvGroup>

                    <AvGroup className='mb-3'>
                        <Label for="description">{t('Description')}</Label>
                        <AvField
                            id="description"
                            name="description"
                            type="textarea"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false }
                            }}
                            defaultValue={documentToEdit?.description}
                        />
                    </AvGroup>

                    <AvGroup className='mb-3'>
                        <Label for="contentType">{t('Content Type')}</Label>
                        <AvField
                            id="contentType"
                            name="contentType"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true }
                            }}
                            defaultValue={documentToEdit?.content_type || 'file'}
                            onChange={(e) => setContentType(e.target.value)}
                            value={contentType}
                        >
                            <option value='file'>
                                {t('File')}
                            </option>

                            <option value='link'>
                                {t('Link')}
                            </option>
                        </AvField>
                    </AvGroup>

                    {contentType === 'file' ? (
                        <FormGroup className='mb-3'>
                            <Label for="file">
                                {t('File Upload')}
                            </Label>
                            <div
                                className='d-flex align-items-center'
                            >
                                <Input
                                    type="file"
                                    name="file"
                                    id="file"
                                    required
                                    onChange={(e) => {
                                        setFile(e.target.files[0])
                                    }}
                                    className='d-none'
                                />
                                <label
                                    htmlFor="file"
                                    className="btn btn-outline-primary me-2"
                                >
                                    {t('Choose File')}
                                </label>
                                {(file || documentToEdit) && <small className="text-muted">
                                    {file?.name || documentToEdit?.originName}
                                </small>}
                            </div>
                            <AvField
                                id="file"
                                name="file"
                                type="hidden"
                                validate={{
                                    required: { value: documentToEdit ? false : true }
                                }}
                                errorMessage={t("This field cannot be blank")}
                                value={file?.name}
                            />
                        </FormGroup>
                    ) : (
                        <AvGroup className='mb-3'>
                            <Label for="path">{t('Path')}</Label>
                            <AvField
                                id="path"
                                name="path"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: true },
                                    pattern: {
                                        value: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                                        errorMessage: t("Please enter a valid URL"),
                                    },
                                }}
                                defaultValue={documentToEdit?.path}
                            />
                        </AvGroup>
                    )}

                    <AvGroup className='mb-3'>
                        <Label for="type">{t('Type')}</Label>
                        <AvField
                            id="type"
                            name="type"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true }
                            }}
                            defaultValue={documentToEdit?.type || 'internal'}
                        >
                            {DocumentsTypes.filter((documentType) => documentType.creatable).map((documentType) => {
                                return (
                                    <option value={documentType.value}>
                                        {t(documentType.title)}
                                    </option>
                                )
                            })}
                        </AvField>
                    </AvGroup>

                    <AvGroup className='mb-3'>
                        <Label for="category">{t('Category')}</Label>
                        <AvField
                            id="category"
                            name="category"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true }
                            }}
                            defaultValue={documentToEdit?.category}
                        >
                            <option value="">-- {t('Select Category')} --</option>

                            {DocumentsCategoryTypes.map((categoryType) => {
                                return (
                                    <option value={categoryType.value}>
                                        {t(categoryType.title)}
                                    </option>
                                )
                            })}
                        </AvField>
                    </AvGroup>

                    <AvGroup className='mb-3'>
                        <Label for="confidentialityLevel">{t('Confidentiality Level')}</Label>
                        <AvField
                            id="confidentialityLevel"
                            name="confidentialityLevel"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true }
                            }}
                            defaultValue={documentToEdit?.confidentialityLevel}
                        >
                            <option value="">-- {t('Select Level')} --</option>

                            {DocumentsConfidentialityLevels.map((level) => {
                                return (
                                    <option value={level.value}>
                                        {t(level.title)}
                                    </option>
                                )
                            })}
                        </AvField>
                    </AvGroup>

                    <AvGroup className='mb-3'>
                        <Label for="legalEntities">
                            {t('Legal Entities')}
                        </Label>

                        <MultiSelect
                            placeholder={t("Select") + "..."}
                            selectedValues={selectedLegalEntities}
                            options={legalEntitiesOptions}
                            isDisabled={fetchingLegalEntities}
                            onChange={(selectedValues) => setSelectedLegalEntities(selectedValues)}
                        />

                        <AvField 
                            type="hidden"
                            name="legalEntities"
                            value={selectedLegalEntities}
                            validate={{
                                required: { value: true }
                            }}
                            errorMessage={t("This field cannot be blank")}
                        />
                    </AvGroup>
                    
                    {ismsControlsOptions ? (
                        <ISMSControls
                            controlsOptions={ismsControlsOptions}
                            onControlsChange={setSelectedControls}
                            defaultValues={selectedControls}
                        />
                    ) : null}
                </ModalBody>
                <ModalFooter>
                    <Button 
                        color="primary" 
                        type="submit"
                        disabled={addingDocumentInProcess || editingDocumentInProcess}
                    >
                        {(addingDocumentInProcess || editingDocumentInProcess) && (
                            <Spinner 
                                color='white'
                                size='sm'
                                className='me-1'
                            />
                        )}
                        {t(documentToEdit ? 'Update' : 'Add')}
                    </Button>

                    <Button 
                        color="secondary" 
                        onClick={toggle}
                        disabled={addingDocumentInProcess || editingDocumentInProcess}
                    >
                        {t('Cancel')}
                    </Button>
                </ModalFooter>
            </AvForm>
        </Modal>
    );
};

const mapStatetoProps = (state) => {
    const { Organization } = state;
    return {
        organization: Organization
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        NewDocumentModal
    )
);